import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DescriptionIcon from "@mui/icons-material/Description";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import { Button } from "@mui/material";
import InteriorDoor from "./Door/InteriorDoor";
import ExteriorDoor from "./Door/ExteriorDoor";
import CustomWindowForm from "./WindowDoor/CustomWindowForm";
import StepRail from "../../components/Forms/RFQ/StepRail";
import CustomerStep from "../../components/Forms/RFQ/CustomerStep";
import FormTypeStep from "../../components/Forms/RFQ/FormTypeStep";
import SelectFormStep from "../../components/Forms/RFQ/SelectFormStep";
import SelectionSummary from "../../components/Forms/RFQ/SelectionSummary";
import {
  DEFAULT_MARGIN,
  isMarginValid,
} from "../../components/Forms/RFQ/margin";
import { makeid } from "../../components/Forms/api";
import { selectUser } from "../../features/userSlice";
import { PageHeader, PageShell } from "../../components/UI/Page";

const STEP_COPY = {
  1: {
    label: "Customer",
    title: "Who is this for?",
    blurb:
      "Search Bistrack to pull in an existing account, or enter the details by hand.",
  },
  2: {
    label: "Quote or order",
    title: "Quote or order?",
    blurb:
      "This sets how the form is submitted. To change the margin, click the Margin box above.",
  },
  3: {
    label: "Choose form",
    title: "Pick a form",
    blurb: "Choosing a form opens it with everything above already filled in.",
  },
};

const QuoteOrderForm = () => {
  /*
  formData carries customer info (account number, job number, address),
  whether this is a quote or an order, and the margin, through to whichever
  form the user picks in step 3.
  */
  const user = useSelector(selectUser);

  const [formData, setFormData] = useState({
    ref_id: makeid(8),
    // Optional-chained: on a fresh deep link this renders once with a null
    // user before the auth redirect; the page remounts with the real user
    // after login, re-running this initializer.
    userID: user?.userID,
    form_type: "quote",
    margin: DEFAULT_MARGIN,
  });

  const [step, setStep] = useState(1);
  // Steps the user has already passed through keep their checkmark even after
  // jumping back, so the rail doesn't appear to lose progress.
  const [furthestStep, setFurthestStep] = useState(1);
  const [formSelected, setFormSelected] = useState(null);
  const [selectedWindowForm, setSelectedWindowForm] = useState(null);

  const customerComplete = !!(
    String(formData.customer || "").trim() ||
    String(formData.account || "").trim()
  );
  const detailsComplete =
    !!formData.form_type && isMarginValid(formData.margin);

  const goToStep = (next) => {
    setStep(next);
    setFurthestStep((furthest) => Math.max(furthest, next));
  };

  const steps = [
    {
      label: STEP_COPY[1].label,
      summary: customerComplete
        ? formData.customer || `Account #${formData.account}`
        : "Search or enter a customer",
      complete: customerComplete,
      reachable: true,
    },
    {
      label: STEP_COPY[2].label,
      // No margin here on purpose — the rail is on screen for the whole flow,
      // including step 3, and a customer may be watching. Step 2 owns the
      // reveal.
      summary: detailsComplete
        ? formData.form_type === "order"
          ? "Order"
          : "Quote"
        : "Quote or order",
      complete: customerComplete && detailsComplete && furthestStep >= 3,
      reachable: customerComplete,
    },
    {
      label: STEP_COPY[3].label,
      summary: "Pick a form",
      complete: false,
      reachable: customerComplete && detailsComplete,
    },
  ];

  // Why Continue is unavailable, stated on the page rather than left to a
  // greyed-out button. The margin branch is a backstop — MarginChip reverts an
  // invalid value when its editor closes, so it shouldn't be reachable.
  const blockedReason =
    step === 1 && !customerComplete
      ? "Add a customer name or account number to continue."
      : step === 2 && !detailsComplete
      ? "Fix the margin in the box above to continue."
      : "";

  const handleSelectForm = ({ target, formId, formName, definition }) => {
    setFormData((prev) => ({
      ...prev,
      form_id: formId,
      form_name: formName,
      custom_fields: prev?.custom_fields || {},
    }));
    if (definition) setSelectedWindowForm(definition);
    setFormSelected(target);
  };

  const backToPicker = () => {
    setFormSelected(null);
    setSelectedWindowForm(null);
  };

  if (formSelected) {
    return (
      <>
        {/* CustomWindowForm renders its own "Back to Form Selection" control. */}
        {formSelected !== "custom-window-form" && (
          <div className="mx-auto max-w-6xl px-4 pt-6 sm:px-6">
            <button
              type="button"
              onClick={backToPicker}
              className="app-btn app-btn-secondary app-btn-sm"
            >
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              Back to form selection
            </button>
          </div>
        )}

        {formSelected === "interior-door" && (
          <InteriorDoor formData={formData} setFormData={setFormData} />
        )}
        {/* Not offered by the picker yet, kept wired for when it ships. */}
        {formSelected === "exterior-door" && (
          <ExteriorDoor formData={formData} setFormData={setFormData} />
        )}
        {formSelected === "custom-window-form" && (
          <CustomWindowForm
            formData={formData}
            setFormData={setFormData}
            formDefinition={selectedWindowForm}
            onClose={backToPicker}
          />
        )}
      </>
    );
  }

  return (
    <PageShell size="md">
      <PageHeader
        icon={RequestQuoteIcon}
        tone="sky"
        eyebrow="Forms"
        title="New Request for Quote"
        description="Set the customer and pricing once, then pick the form to build."
        actions={
          <Button
            component={Link}
            to="/forms/viewall"
            variant="outlined"
            startIcon={<DescriptionIcon />}
          >
            My forms &amp; drafts
          </Button>
        }
      />

      <StepRail
        steps={steps}
        currentStep={step}
        onStepSelect={(next) => {
          if (steps[next - 1].reachable) goToStep(next);
        }}
      />

      <div className="app-card mt-4 rounded-2xl border p-4 sm:p-6">
        <div className="mb-5">
          <p className="app-accent text-xs font-semibold uppercase tracking-wider">
            Step {step} of 3
          </p>
          <h2 className="mt-0.5 text-lg font-semibold">
            {STEP_COPY[step].title}
          </h2>
          <p className="app-muted mt-1 text-sm">{STEP_COPY[step].blurb}</p>
        </div>

        {step > 1 && (
          <SelectionSummary
            formData={formData}
            setFormData={setFormData}
            onEditStep={goToStep}
            className="mb-6"
          />
        )}

        {step === 1 && (
          <CustomerStep formData={formData} setFormData={setFormData} />
        )}
        {step === 2 && (
          <FormTypeStep formData={formData} setFormData={setFormData} />
        )}
        {step === 3 && <SelectFormStep onSelectForm={handleSelectForm} />}
      </div>

      {/* Step actions float at the bottom of the viewport while the step body
          scrolls, so Continue is always within reach on a phone. Only buttons
          live in here, so the glass blur can't re-anchor anything fixed. */}
      <div data-bottom-bar="actions" className="app-glass app-glass-rim sticky bottom-3 z-10 mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-3 py-2.5 sm:px-4">
        <button
          type="button"
          onClick={() => goToStep(step - 1)}
          disabled={step === 1}
          className="app-btn app-btn-ghost"
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          Back
        </button>

        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-end gap-3">
          {blockedReason && (
            <p className="app-muted text-right text-sm">{blockedReason}</p>
          )}
          {step === 3 ? (
            <p className="app-muted text-right text-sm">
              Pick a form above to start building.
            </p>
          ) : (
            <button
              type="button"
              onClick={() => goToStep(step + 1)}
              disabled={!!blockedReason}
              className="app-btn app-btn-primary"
            >
              Continue
              <ArrowForwardIcon sx={{ fontSize: 16 }} />
            </button>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default QuoteOrderForm;
